// Shared canvas infrastructure for the Atlas views: camera (pan/zoom in a
// square [0,1]² world), design-token resolution for canvas painting, color
// math, and small drawing helpers. Views own their render loops; this file
// owns the geometry + palette plumbing so all three views feel identical.

export interface Camera {
  panX: number;
  panY: number;
  k: number;
}

export const newCamera = (): Camera => ({ panX: 0, panY: 0, k: 1 });

/** Pixels per world-unit at this camera: the [0,1]² world fits the short side. */
export function viewScale(cam: Camera, w: number, h: number): number {
  return Math.min(w, h) * 0.92 * cam.k;
}

export function worldToScreen(wx: number, wy: number, cam: Camera, w: number, h: number): [number, number] {
  const s = viewScale(cam, w, h);
  return [w / 2 + (wx - 0.5) * s + cam.panX, h / 2 + (wy - 0.5) * s + cam.panY];
}

export function screenToWorld(sx: number, sy: number, cam: Camera, w: number, h: number): [number, number] {
  const s = viewScale(cam, w, h);
  return [(sx - w / 2 - cam.panX) / s + 0.5, (sy - h / 2 - cam.panY) / s + 0.5];
}

/** 2–98% quantile bounds of a filtered point set — outliers shouldn't shrink the world. */
export function quantileBounds(points: { x: number; y: number }[], visible: boolean[]): [number, number, number, number] | null {
  const xs: number[] = [];
  const ys: number[] = [];
  for (let i = 0; i < points.length; i++) {
    if (!visible[i]) continue;
    xs.push(points[i].x);
    ys.push(points[i].y);
  }
  if (xs.length === 0) return null;
  xs.sort((a, b) => a - b);
  ys.sort((a, b) => a - b);
  const q = (arr: number[], p: number) => arr[Math.max(0, Math.min(arr.length - 1, Math.round(p * (arr.length - 1))))];
  return [q(xs, 0.02), q(ys, 0.02), q(xs, 0.98), q(ys, 0.98)];
}

/** Camera that frames a world-space bounding box centered with breathing room. */
export function fitCameraToBounds(minX: number, minY: number, maxX: number, maxY: number, w: number, h: number): Camera {
  const bw = Math.max(0.05, maxX - minX);
  const bh = Math.max(0.05, maxY - minY);
  const cx = (minX + maxX) / 2;
  const cy = (minY + maxY) / 2;
  const base = Math.min(w, h) * 0.92; // px per world unit at k=1
  const k = Math.min(16, Math.max(0.45, Math.min((w * 0.86) / (base * bw), (h * 0.86) / (base * bh))));
  const s = base * k;
  return { k, panX: -(cx - 0.5) * s, panY: -(cy - 0.5) * s };
}

/** Zoom keeping the world point under the cursor fixed on screen. */
export function zoomAt(cam: Camera, sx: number, sy: number, factor: number, w: number, h: number): Camera {
  const k = Math.min(16, Math.max(0.45, cam.k * factor));
  const [wx, wy] = screenToWorld(sx, sy, cam, w, h);
  const s = Math.min(w, h) * 0.92 * k;
  return { k, panX: sx - w / 2 - (wx - 0.5) * s, panY: sy - h / 2 - (wy - 0.5) * s };
}

// ---------------------------------------------------------------------------
// Design tokens → canvas paint. Canvas can't read CSS vars, so resolve them
// once per mount from the live stylesheet (fallbacks mirror tokens.css light).
// ---------------------------------------------------------------------------

export interface Tokens {
  surface: string;
  surfaceMuted: string;
  border: string;
  borderStrong: string;
  rowDivider: string;
  track: string;
  ink: string;
  inkStrong: string;
  textSecondary: string;
  textTertiary: string;
  textMuted: string;
  teal: string;
  tealInk: string;
  tealTint: string;
  statusGreen: string;
  statusAmber: string;
  statusRed: string;
}

export function readTokens(): Tokens {
  const cs = getComputedStyle(document.documentElement);
  const v = (name: string, fallback: string) => cs.getPropertyValue(name).trim() || fallback;
  return {
    surface: v("--kt-surface", "#ffffff"),
    surfaceMuted: v("--kt-surface-muted", "#fafbfb"),
    border: v("--kt-border", "#e6eaec"),
    borderStrong: v("--kt-border-strong", "#d3d9dc"),
    rowDivider: v("--kt-row-divider", "#f0f3f4"),
    track: v("--kt-track", "#e8ebed"),
    ink: v("--kt-ink", "#1a1f23"),
    inkStrong: v("--kt-ink-strong", "#2c3338"),
    textSecondary: v("--kt-text-secondary", "#4a5258"),
    textTertiary: v("--kt-text-tertiary", "#6a7177"),
    textMuted: v("--kt-text-muted", "#9aa0a6"),
    teal: v("--kt-teal", "#008e92"),
    tealInk: v("--kt-teal-ink", "#0c6f68"),
    tealTint: v("--kt-teal-tint", "#dcedea"),
    statusGreen: v("--kt-status-active", "#16a34a"),
    statusAmber: v("--kt-status-paused", "#d97706"),
    statusRed: v("--kt-spine-red", "#e5484d"),
  };
}

// ---------------------------------------------------------------------------
// Color math (hex or rgb() strings, as tokens resolve to either).
// ---------------------------------------------------------------------------

export function parseColor(c: string): [number, number, number] {
  const s = c.trim();
  if (s.startsWith("#")) {
    const hex = s.slice(1);
    const n = hex.length === 3 ? hex.split("").map((ch) => ch + ch).join("") : hex;
    return [parseInt(n.slice(0, 2), 16), parseInt(n.slice(2, 4), 16), parseInt(n.slice(4, 6), 16)];
  }
  const m = s.match(/rgba?\(([^)]+)\)/);
  if (m) {
    const [r, g, b] = m[1].split(",").map((x) => parseFloat(x));
    return [r || 0, g || 0, b || 0];
  }
  return [106, 113, 119]; // --kt-text-tertiary fallback
}

export function rgba(c: string, a: number): string {
  const [r, g, b] = parseColor(c);
  return `rgba(${r},${g},${b},${a})`;
}

export function lerpColor(a: string, b: string, t: number): string {
  const ca = parseColor(a);
  const cb = parseColor(b);
  const q = Math.max(0, Math.min(1, t));
  return `rgb(${Math.round(ca[0] + (cb[0] - ca[0]) * q)},${Math.round(ca[1] + (cb[1] - ca[1]) * q)},${Math.round(ca[2] + (cb[2] - ca[2]) * q)})`;
}

// ---------------------------------------------------------------------------
// Drawing + misc helpers.
// ---------------------------------------------------------------------------

export const MONO_10 = '500 10px "Roboto Mono", ui-monospace, monospace';
export const MONO_11 = '500 11px "Roboto Mono", ui-monospace, monospace';

/** Text with a surface-coloured halo so labels stay legible over marks. */
export function drawHaloText(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  fill: string,
  halo: string,
  font: string = MONO_10,
  align: CanvasTextAlign = "center"
): void {
  ctx.font = font;
  ctx.textAlign = align;
  ctx.textBaseline = "middle";
  ctx.lineJoin = "round";
  ctx.strokeStyle = halo;
  ctx.lineWidth = 3;
  ctx.strokeText(text, x, y);
  ctx.fillStyle = fill;
  ctx.fillText(text, x, y);
}

/** Node radius: character count → area, softened by zoom. */
export function atomRadius(chars: number, k: number): number {
  const base = 2.6 + Math.min(5.2, Math.sqrt(Math.max(chars, 40)) / 24);
  return base * Math.pow(k, 0.35);
}

/** Deterministic hash → [0,1) (stable jitter, no Math.random). */
export function hash01(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0) / 4294967296;
}

/** What a view reports on hover; the page renders the one shared tooltip. */
export interface TooltipPayload {
  x: number;
  y: number;
  title: string;
  meta: string;
  swatch?: string;
}

/** Compact age label for mono meta lines. */
export function ageLabel(createdAt: number): string {
  if (!createdAt) return "undated";
  const days = Math.floor((Date.now() - createdAt) / 86400000);
  if (days <= 0) return "today";
  if (days < 28) return `${days}d`;
  return `${Math.floor(days / 7)}w`;
}

export const prefersReducedMotion = (): boolean =>
  typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/** Size a canvas backing store to its CSS box × devicePixelRatio. Returns [cssW, cssH]. */
export function fitCanvas(canvas: HTMLCanvasElement): [number, number] {
  const rect = canvas.getBoundingClientRect();
  const dpr = window.devicePixelRatio || 1;
  const bw = Math.max(1, Math.round(rect.width * dpr));
  const bh = Math.max(1, Math.round(rect.height * dpr));
  if (canvas.width !== bw || canvas.height !== bh) {
    canvas.width = bw;
    canvas.height = bh;
  }
  return [rect.width, rect.height];
}
